export function SalesComparisonApproach() {
  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Method #2: Sales Comparison Approach
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            The sales comparison approach values your apartment building by analyzing recent sales of similar properties in your market. This method provides real-world market validation and helps confirm values derived from the income approach.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/6ea8aba348567c2e055ff9e882cd1be4.jpg"
              alt="Sales comparison approach for apartment building valuation"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">How Sales Comparison Works</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              The sales comparison approach examines recent sales of comparable apartment buildings (called "comps") in your area. By analyzing what similar properties have sold for, you can estimate your property's market value based on actual transaction data rather than theoretical calculations.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              This approach is particularly useful for validating income approach values and understanding current market sentiment. Buyers and sellers ultimately determine market value through actual transactions, making this approach a critical reality check for any valuation.
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-search-2-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Find Comparable Sales</h4>
                  <p className="text-gray-600">Identify 3-5 similar properties sold within the past 6-12 months</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-scales-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Adjust for Differences</h4>
                  <p className="text-gray-600">Account for variations in size, condition, location, and amenities</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-calculator-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Calculate Value Range</h4>
                  <p className="text-gray-600">Determine a likely value range based on adjusted comparable sales</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 lg:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">What Makes a Good Comparable Property?</h3>
          <p className="text-lg text-gray-700 mb-8 text-center max-w-4xl mx-auto leading-relaxed">
            Not all apartment building sales are suitable comparables. The best comps share similar characteristics with your property and represent arm's-length transactions in normal market conditions.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                <i className="ri-map-pin-line text-teal-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2 text-lg">Similar Location</h4>
              <p className="text-gray-600">Same neighborhood or submarket with comparable demographics, schools, and amenities</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                <i className="ri-building-2-line text-teal-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2 text-lg">Similar Size</h4>
              <p className="text-gray-600">Within 20-30% of your property's unit count and total square footage</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                <i className="ri-calendar-line text-teal-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2 text-lg">Recent Sale</h4>
              <p className="text-gray-600">Sold within the past 6-12 months to reflect current market conditions</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                <i className="ri-hammer-line text-teal-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2 text-lg">Similar Condition</h4>
              <p className="text-gray-600">Comparable age, construction quality, and level of maintenance or renovation</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                <i className="ri-home-smile-line text-teal-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2 text-lg">Similar Unit Mix</h4>
              <p className="text-gray-600">Comparable bedroom/bathroom configurations and unit sizes</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                <i className="ri-hand-coin-line text-teal-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2 text-lg">Arm's Length</h4>
              <p className="text-gray-600">Normal market transaction, not distressed sale, family transfer, or special circumstance</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Making Adjustments to Comparables</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              No two properties are exactly alike, so adjustments must be made to account for differences between your property and the comparables. These adjustments help normalize the data and provide a more accurate value estimate for your specific property.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Common adjustments include differences in unit count, square footage, property condition, location quality, parking availability, amenities, and time of sale. Each adjustment should be supported by market data and applied consistently across all comparables.
            </p>
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                <i className="ri-lightbulb-line text-yellow-600"></i>
                Pro Tip
              </h4>
              <p className="text-gray-700">
                If you need to make large adjustments (more than 20-30%) to a comparable, it's probably not a good comp. Look for properties that require minimal adjustments for the most reliable valuation.
              </p>
            </div>
          </div>
          <div>
            <img
              src="https://readdy.ai/api/search-image?query=real-estate-adjustment-grid-spreadsheet-comparable-sales-adjustments-property-valuation-analysis-plus-minus-calculations-appraisal-methodology-professional-comparison-matrix-financial-analysis&width=800&height=600&seq=adjustments-analysis-001&orientation=landscape"
              alt="Comparable sales adjustments for apartment valuation"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
        </div>

        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg p-8 lg:p-12 text-white">
          <h3 className="text-3xl font-bold mb-8 text-center">Sales Comparison Example</h3>
          <p className="text-lg text-center mb-8 max-w-3xl mx-auto opacity-90">
            Here's how to use comparable sales to value a 20-unit apartment building:
          </p>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/20">
                  <th className="pb-4 pr-4 font-bold">Property</th>
                  <th className="pb-4 pr-4 font-bold">Units</th>
                  <th className="pb-4 pr-4 font-bold">Sale Price</th>
                  <th className="pb-4 pr-4 font-bold">Price/Unit</th>
                  <th className="pb-4 pr-4 font-bold">Adjustments</th>
                  <th className="pb-4 font-bold">Adjusted Price/Unit</th>
                </tr>
              </thead>
              <tbody className="text-base">
                <tr className="border-b border-white/10">
                  <td className="py-4 pr-4">Comp #1</td>
                  <td className="py-4 pr-4">18 units</td>
                  <td className="py-4 pr-4">$1,890,000</td>
                  <td className="py-4 pr-4">$105,000</td>
                  <td className="py-4 pr-4">-$3,000 (condition)</td>
                  <td className="py-4 font-semibold">$102,000</td>
                </tr>
                <tr className="border-b border-white/10">
                  <td className="py-4 pr-4">Comp #2</td>
                  <td className="py-4 pr-4">22 units</td>
                  <td className="py-4 pr-4">$2,310,000</td>
                  <td className="py-4 pr-4">$105,000</td>
                  <td className="py-4 pr-4">+$2,000 (location)</td>
                  <td className="py-4 font-semibold">$107,000</td>
                </tr>
                <tr className="border-b border-white/10">
                  <td className="py-4 pr-4">Comp #3</td>
                  <td className="py-4 pr-4">20 units</td>
                  <td className="py-4 pr-4">$2,100,000</td>
                  <td className="py-4 pr-4">$105,000</td>
                  <td className="py-4 pr-4">No adjustment</td>
                  <td className="py-4 font-semibold">$105,000</td>
                </tr>
                <tr>
                  <td className="py-4 pr-4">Comp #4</td>
                  <td className="py-4 pr-4">24 units</td>
                  <td className="py-4 pr-4">$2,520,000</td>
                  <td className="py-4 pr-4">$105,000</td>
                  <td className="py-4 pr-4">-$2,000 (age)</td>
                  <td className="py-4 font-semibold">$103,000</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-8 bg-white/10 rounded-lg p-6">
            <h4 className="text-xl font-bold mb-4">Valuation Conclusion:</h4>
            <p className="text-lg mb-2">Average Adjusted Price Per Unit: <strong>$104,250</strong></p>
            <p className="text-lg mb-2">Your Property: 20 units</p>
            <p className="text-2xl font-bold border-t border-white/30 pt-4 mt-4">
              Estimated Value: 20 × $104,250 = <strong>$2,085,000</strong>
            </p>
            <p className="text-base opacity-90 mt-4">
              This value should be compared with the income approach value to arrive at a final market value estimate.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}